import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';

const riskColors: Record<string, string> = {
  critical: 'text-red-600 bg-red-50 border-red-200',
  high: 'text-orange-600 bg-orange-50 border-orange-200',
  moderate: 'text-amber-600 bg-amber-50 border-amber-200',
  low: 'text-green-600 bg-green-50 border-green-200',
};

const tabs = [
  'Overview',
  'Timeline',
  'Evidence',
  'Documents',
  'Referrals',
  'Inter-Agency Notes'
];

export default function ChildProfileDetail() {
  const { case_number } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Overview');
  const [evidence, setEvidence] = useState<any[]>([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [referralAgency, setReferralAgency] = useState('');
  const [referralPurpose, setReferralPurpose] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<any>(null);

  const updateEdit = (key: string, value: any) =>
  setEditForm((prev: any) => ({ ...prev, [key]: value }));

  const getRisk = (child: any) => {
    let score = 75;

    switch (child.incidentType) {
      case 'Sexual Abuse':
      case 'Child Trafficking':
      case 'Online Sexual Exploitation':
        score = 95;
        break;

      case 'Physical Abuse':
        score = 90;
        break;

      case 'Emotional Abuse':
        score = 80;
        break;

      case 'Neglect':
      case 'Child Labor':
      case 'Abandonment':
        score = 75;
        break;

      default:
        score = 70;
    }

    if (child.priorIncidents === 'yes') score += 5;
    if (child.priorityLevel === 'urgent') score += 3;
    if (child.priorityLevel === 'emergency') score += 5;

    // cap at 100
    if (score > 100) score = 100;

    let level = 'low';
    if (score >= 90) level = 'critical';
    else if (score >= 80) level = 'high';
    else if (score >= 75) level = 'moderate';

    return { score, level };
  };

  const handleUpdate = async () => {
    const res = await fetch("https://guardiancloudcare.com/backend/api/update_child.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(editForm)
    });

    const data = await res.json();

    if (data.success) {
      setChild(editForm); 
      setShowEditModal(false);
      setSuccessMsg("Profile updated successfully!");

      setTimeout(() => setSuccessMsg(''), 2000);
    }
  };


    useEffect(() => {
      if (!case_number) return;
      const loadData = async () => {
        setLoading(true);
        setEvidenceLoading(true);
        try {

          const childRes = await fetch(
            `https://guardiancloudcare.com/backend/api/child_full_details.php?case_number=${encodeURIComponent(case_number)}`
          );

          const childText = await childRes.text();

          if (childText.startsWith("<br")) {
            throw new Error(childText);
          }

          let childData;

          try {
            childData = JSON.parse(childText);
          } catch (err) {
            console.error(
              "Invalid child profile response:",
              childText
            );

            throw new Error(
              "Child profile API returned invalid JSON."
            );
          }

          if (!childData.success) {

            setChild(null);
            setLoading(false);

          } else {

            setChild({
              ...childData.data,

              timeline:
                childData.timeline || [],

              documents:
                childData.documents || [],

              referrals:
                childData.referrals || [],

              interAgencyNotes:
                childData.interAgencyNotes || []
            });

            setLoading(false);
          }

          const evidenceRes = await fetch(
            `https://guardiancloudcare.com/backend/api/get_case_evidence.php?case_number=${encodeURIComponent(case_number)}`
          );

          const evidenceText =
            await evidenceRes.text();

          if (evidenceText.startsWith("<br")) {

            console.error(
              "Evidence API PHP error:",
              evidenceText
            );

            setEvidence([]);

          } else {

            try {

              const evidenceData =
                JSON.parse(evidenceText);

              if (evidenceData.success) {

                setEvidence(
                  evidenceData.evidence || []
                );

              } else {

                console.error(
                  "Evidence error:",
                  evidenceData.error
                );

                setEvidence([]);
              }

            } catch (err) {

              console.error(
                "Invalid evidence response:",
                evidenceText
              );

              setEvidence([]);
            }
          }

        } catch (err) {

          console.error(
            "Fetch error:",
            err
          );

          setLoading(false);
          setEvidence([]);

        } finally {

          setEvidenceLoading(false);

        }

      };

      loadData();

    }, [case_number]);

   if (loading) {
    return (
      <AppLayout title="Child Profile" breadcrumb={['Home', 'Child Profiles']}>
        <div className="text-center py-20 text-gray-400">Loading...</div>
      </AppLayout>
    );
  }

  if (!child) {
    return (
      <AppLayout title="Child Profile" breadcrumb={['Home', 'Child Profiles', 'Not Found']}>
        <div className="text-center py-20">
          <i className="ri-user-unfollow-line text-5xl text-gray-300 mb-4 block" />
          <p className="text-gray-500">Child profile not found.</p>
          <button 
            onClick={() => navigate('/child-profiles')} 
            className="mt-4 text-teal-500 text-sm cursor-pointer"
          >
            Back to profiles
          </button>
        </div>
      </AppLayout>
    );
  }

  const risk = getRisk(child);

  const riskPercent = risk.score;

  const riskColor =
    risk.level === 'critical'
      ? '#ef4444'
      : risk.level === 'high'
      ? '#f97316'
      : risk.level === 'moderate'
      ? '#f59e0b'
      : '#22c55e';

  return (
    <AppLayout title={`${child.firstName} ${child.lastName}`} breadcrumb={['Home', 'Child Profiles', `${child.firstName} ${child.lastName}`]}>
     {successMsg && (
      <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg text-sm">
        {successMsg}
      </div>
    )}
      {/* Profile Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">

        <div className="flex flex-col md:flex-row items-start gap-6">
          <div className="relative flex-shrink-0 group">
            <img
              src={child.profile}
              alt="Profile"
              className="w-24 h-24 rounded-full object-cover border"
            />

            {/* Risk Badge */}
            <span className={`absolute -bottom-2 -right-2 text-xs px-2 py-0.5 rounded-full border font-medium capitalize ${riskColors[risk.level]}`}>
              {risk.level}
            </span>

            {/* Upload Overlay Button */}
            <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition cursor-pointer">
              <i className="ri-camera-line text-white text-lg"></i>
              
              <input
                type="file"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;

                  const formData = new FormData();
                  formData.append("file", file);
                  formData.append("id", String(child.id));

                  const res = await fetch(
                      "https://guardiancloudcare.com/backend/api/upload_profile.php",
                      {
                          method: "POST",
                          body: formData
                      }
                  );

                  const data = await res.json();

                  if (data.success) {

                      setChild((prev:any)=>({
                          ...prev,
                          profile: data.path
                      }));

                      setSuccessMsg("Profile uploaded successfully!");

                      setTimeout(()=>{
                          setSuccessMsg("");
                      },2000);

                  } else {

                      alert(data.error);

                  }
                }}
              />
            </label>
          </div>
          <div className="flex-1">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{child.firstName} {child.middleName} {child.lastName}</h2>
                <div className="flex flex-wrap items-center gap-3 mt-1">
                  <span className="text-sm text-gray-500 flex items-center gap-1"><i className="ri-calendar-line" />{child.age} years old</span>
                  <span className="text-sm text-gray-500 flex items-center gap-1"><i className="ri-user-line" />{child.gender}</span>
                  <span className="text-sm text-gray-500 flex items-center gap-1"><i className="ri-map-pin-line" />{child.municipality}, {child.province}</span>
                </div>
                <p className="text-xs font-mono text-teal-600 mt-1 flex items-center gap-1">
                  <i className="ri-file-list-3-line" />{child.caseNumber}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setEditForm(child);
                    setShowEditModal(true);
                  }}
                  className="bg-teal-500 text-white px-4 py-2 rounded-lg text-sm"
                >
                  Edit Child Profile
                </button>
                <button onClick={() => navigate('/risk-assessment')} className="text-sm bg-teal-500 text-white px-4 py-2 rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                  <i className="ri-shield-check-line" />Risk Assessment
                </button>
                <button onClick={() => setShowReferralModal(true)} className="text-sm border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                  <i className="ri-share-forward-line" />Refer
                </button>
                <button onClick={() => setShowArchiveModal(true)} className="text-sm border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                  <i className="ri-archive-line" />Archive
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white rounded-xl border border-gray-100 p-1 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap
              ${activeTab === tab ? 'bg-teal-500 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'Overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><i className="ri-user-line text-teal-500" />Personal Information</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ['Full Name', `${child.firstName} ${child.middleName} ${child.lastName}`],
                  ['Nickname', child.nickname],
                  ['Date of Birth', child.birthDate],
                  ['Age', `${child.age} years old`],
                  ['Gender', child.gender],
                  ['Nationality', child.nationality],
                  ['Religion', child.religion],
                  ['Civil Status', child.civilStatus],
                  ['Address', `${child.address}, ${child.barangay}`],
                  ['Municipality', `${child.municipality}, ${child.province}`],
                  ['School', child.schoolName],
                  ['Grade Level', child.gradeLevel],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-xs text-gray-400">{label}</p>
                    <p className="font-medium text-gray-800">{value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><i className="ri-home-heart-line text-teal-500" />Guardian & Family</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ['Guardian Name', child.guardianName],
                  ['Relationship', child.guardianRelationship],
                  ['Contact', child.guardianContact],
                  ['Father', child.fatherName],
                  ['Mother', child.motherName],
                  ['Siblings', String(child.siblings)],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-xs text-gray-400">{label}</p>
                    <p className="font-medium text-gray-800">{value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><i className="ri-alarm-warning-line text-red-500" />Incident Details</h4>
              <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                <div><p className="text-xs text-gray-400">Incident Type</p><p className="font-medium text-gray-800">{child.incidentType}</p></div>
                <div><p className="text-xs text-gray-400">Incident Date</p><p className="font-medium text-gray-800">{child.incidentDate}</p></div>
              </div>
              <div><p className="text-xs text-gray-400 mb-1">Description</p><p className="text-sm text-gray-700 leading-relaxed">{child.incidentDescription}</p></div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
              <h4 className="font-semibold text-gray-800 mb-4 text-sm">Risk Score</h4>
              <div className="relative w-32 h-32 mx-auto mb-3">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f3f4f6" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke={riskColor} strokeWidth="3"
                    strokeDasharray={`${riskPercent} ${100 - riskPercent}`} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold text-gray-900">{riskPercent}</span>
                  <span className="text-xs text-gray-400">/ 100</span>
                </div>
              </div>
              <span className={`text-sm px-3 py-1 rounded-full border font-semibold capitalize ${riskColors[risk.level]}`}>{risk.level} Risk </span>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">

              <h4 className="font-semibold text-gray-800 mb-3 text-sm">
                Assigned Worker
              </h4>

              <div className="flex items-center gap-3">

                <div className="w-12 h-12 rounded-full overflow-hidden border border-gray-200 flex-shrink-0">

                  {child.workerProfile ? (

                    <img
                      src={child.workerProfile}
                      alt="Assigned Worker"
                      className="w-full h-full object-cover"
                    />

                  ) : (

                    <div className="w-full h-full bg-teal-500 flex items-center justify-center text-white font-bold text-sm">
                      {child.assignedWorker?.charAt(0) || 'U'}
                    </div>

                  )}

                </div>

                <div>

                  <p className="text-sm font-medium text-gray-800">
                    {child.assignedWorker || 'Not Assigned'}
                  </p>

                  <p className="text-xs text-gray-400 capitalize">
                    {(child.workerRole || 'social_worker').replace('_', ' ')}
                  </p>

                  <p className="text-xs text-gray-500 mt-0.5">
                    {child.workerPhone || 'No contact number'}
                  </p>

                </div>

              </div>

            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h4 className="font-semibold text-gray-800 mb-3 text-sm">Medical Info</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-xs text-gray-400">Conditions</p>

                  <div className="flex flex-wrap gap-1 mt-1">
                    {child.medicalConditions
                      ? child.medicalConditions.split(',').map((c: string, i: number) => (
                          <span key={i} className="bg-red-50 text-red-600 text-xs px-2 py-0.5 rounded-full border border-red-200">
                            {c.trim()}
                          </span>
                        ))
                      : <span className="text-gray-400 text-sm">None</span>}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-400">Medications</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {child.medications
                      ? child.medications.split(',').map((m: string, i: number) => (
                          <span key={i} className="bg-blue-50 text-blue-600 text-xs px-2 py-0.5 rounded-full border border-blue-200">
                            {m.trim()}
                          </span>
                        ))
                      : <span className="text-gray-400 text-sm">None</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timeline Tab */}
      {activeTab === 'Timeline' && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h4 className="font-semibold text-gray-800 mb-6">Case Timeline & Activity Log</h4>
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-100" />
            <div className="space-y-6">
              {child.timeline.map((item, i) => (
                <div key={i} className="flex gap-4 relative">
                  <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0 z-10">
                    <i className="ri-check-line text-white text-xs" />
                  </div>
                  <div className="flex-1 bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-sm font-semibold text-gray-800">{item.action}</p>
                      <span className="text-xs text-gray-400 whitespace-nowrap">{item.date}</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">By: {item.user}</p>
                    <p className="text-sm text-gray-600">{item.notes}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-700 mb-2">Add Activity Note</p>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Enter activity notes..."
              rows={3}
              maxLength={500}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-none"
            />
            <div className="flex justify-end mt-2">
              <button
                onClick={async () => {
                  await fetch("https://guardiancloudcare.com/backend/api/add_timeline.php", {
                    method: "POST",
                    headers: {"Content-Type":"application/json"},
                    body: JSON.stringify({
                      caseNumber: child.caseNumber,
                      action: "Manual Note",
                      notes: newNote,
                      user: "Admin"
                    })
                  });

                  setNewNote('');

                  // reload
                  window.location.reload();
                }}
                className="bg-teal-500 text-white text-sm px-4 py-2 rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Add Note
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'Evidence' && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          {/* HEADER */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
            <div>
              <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                <i className="ri-folder-image-line text-teal-500" />
                Case Evidence
              </h4>
              <p className="text-xs text-gray-400 mt-1">
                Photos and videos submitted as evidence for this case.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full">
                {evidence.length} file
                {evidence.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          {/* LOADING */}
          {evidenceLoading ? (
            <div className="text-center py-16">
              <i className="ri-loader-4-line animate-spin text-3xl text-teal-500" />
              <p className="text-sm text-gray-400 mt-3">
                Loading case evidence...
              </p>
            </div>
          ) : evidence.length === 0 ? (
            /* EMPTY */
            <div className="text-center py-16 text-gray-400">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-folder-open-line text-3xl text-gray-300" />
              </div>
              <p className="text-sm font-medium text-gray-500">
                No evidence uploaded
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Images and videos uploaded during Case Intake will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-8">

              {evidence.filter(
                (item) => item.type === 'image'
              ).length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
                      <i className="ri-image-line text-teal-500" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">
                        Images
                      </h5>
                      <p className="text-xs text-gray-400">
                        Evidence photographs
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {evidence
                      .filter(
                        (item) => item.type === 'image'
                      )
                      .map((item) => {

                        const fileUrl =
                          item.source === 'victim_report'
                            ? `https://guardiancloudcare.com/backend/uploads/victim_reports/${encodeURIComponent(item.file)}`
                            : `https://guardiancloudcare.com/backend/uploads/case_evidence/${encodeURIComponent(item.file)}`;

                        return (
                          <div
                            key={item.id}
                            className="group bg-gray-50 border border-gray-100 rounded-xl overflow-hidden hover:border-teal-200 hover:shadow-sm transition"
                          >
                            {/* IMAGE */}
                            <div className="relative aspect-square bg-gray-100 overflow-hidden">
                              <img
                                src={fileUrl}
                                alt={item.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                              {/* VIEW */}
                              <a
                                href={fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition"
                              >
                                <span className="opacity-0 group-hover:opacity-100 w-10 h-10 rounded-full bg-white text-gray-700 flex items-center justify-center shadow-lg transition">

                                  <i className="ri-eye-line" />
                                </span>
                              </a>
                            </div>
                            {/* INFO */}
                            <div className="p-3">
                              <p
                                className="text-xs font-medium text-gray-700 truncate"
                                title={item.name}
                              >
                                {item.name}
                              </p>
                              <p className="text-[11px] text-gray-400 mt-1">
                                {item.size}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {evidence.filter(
                (item) => item.type === 'video'
              ).length > 0 && (

                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                      <i className="ri-video-line text-purple-500" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">
                        Video Evidence
                      </h5>
                      <p className="text-xs text-gray-400">
                        Video files submitted with this case
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {evidence
                      .filter(
                        (item) => item.type === 'video'
                      )
                      .map((item) => {
                        const fileUrl =
                          item.source === 'victim_report'
                            ? `https://guardiancloudcare.com/backend/uploads/victim_reports/${encodeURIComponent(item.file)}`
                            : `https://guardiancloudcare.com/backend/uploads/case_evidence/${encodeURIComponent(item.file)}`;

                        return (
                          <div
                            key={item.id}
                            className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden"
                          >
                            {/* VIDEO */}
                            <div className="bg-black">
                              <video
                                controls
                                preload="metadata"
                                className="w-full max-h-[420px]"
                              >
                                <source
                                  src={fileUrl}
                                  type={item.mimeType}
                                />
                                Your browser does not support
                                video playback.
                              </video>
                            </div>
                            {/* INFO */}
                            <div className="p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p
                                    className="text-sm font-semibold text-gray-800 truncate"
                                    title={item.name}
                                  >
                                    {item.name}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {item.size}
                                    {' · '}
                                    {item.date}
                                  </p>
                                </div>
                                <a
                                  href={fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex-shrink-0 w-9 h-9 rounded-lg bg-purple-50 text-purple-500 flex items-center justify-center hover:bg-purple-100"
                                >
                                  <i className="ri-external-link-line" />
                                </a>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* PRIVACY NOTICE */}
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex gap-3">
              <i className="ri-shield-check-line text-amber-600 text-lg flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-amber-800">
                  Confidential Case Evidence
                </p>
                <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                  These files contain sensitive case information
                  and should only be accessed by authorized personnel.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Documents Tab */}
        {activeTab === 'Documents' && (
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            
            <div className="flex items-center justify-between mb-6">
              <h4 className="font-semibold text-gray-800">Case Documents</h4>

              <label className="bg-teal-500 text-white text-sm px-4 py-2 rounded-lg cursor-pointer hover:bg-teal-600 transition-colors whitespace-nowrap flex items-center gap-2">
                <i className="ri-upload-line" />Upload Document

                <input
                  type="file"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    const formData = new FormData();
                    formData.append("file", file);
                    formData.append("case_number", child.caseNumber);

                    const res = await fetch("https://guardiancloudcare.com/backend/api/upload_document.php", {
                      method: "POST",
                      body: formData
                    });

                    const data = await res.json();

                    if (data.success) {
                      setSuccessMsg("Document uploaded!");
                      setTimeout(() => setSuccessMsg(''), 2000);
                      window.location.reload();
                    } else {
                      alert("Upload failed");
                    }
                  }}
                />
              </label>
            </div>

            {/* EMPTY STATE */}
            {child.documents.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <i className="ri-file-list-3-line text-4xl mb-2 block" />
                <p className="text-sm">No documents uploaded</p>
              </div>
            ) : (

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {child.documents.map((doc: any) => (
                  
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100 hover:border-teal-200 transition-colors"
                  >

                    {/* ICON */}
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${doc.type === 'PDF' ? 'ri-file-pdf-line' : 'ri-image-line'} text-teal-600`} />
                    </div>

                    {/* INFO */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{doc.name}</p>
                      <p className="text-xs text-gray-400">
                        {doc.type} · {doc.size} · {doc.date}
                      </p>
                    </div>

                    {/* ACTIONS */}
                    <div className="flex gap-1">

                      <a
                        href={`https://guardiancloudcare.com/backend/uploads/${doc.file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-500 rounded-lg hover:bg-teal-50"
                      >
                        <i className="ri-eye-line text-sm" />
                      </a>

                      {/* DELETE */}
                      <button
                        onClick={async () => {
                          if (!confirm("Delete this document?")) return;

                          await fetch("https://guardiancloudcare.com/backend/api/delete_document.php", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ id: doc.id })
                          });

                          setSuccessMsg("Document deleted!");
                          setTimeout(() => setSuccessMsg(''), 2000);
                          window.location.reload();
                        }}
                        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>

                    </div>
                  </div>

                ))}
              </div>
            )}

            {/* SECURITY NOTICE */}
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700">
                <i className="ri-shield-line mr-1" />
                All documents are encrypted per RA 10173 (Data Privacy Act of 2012).
              </p>
            </div>

          </div>
        )}

      {/* Referrals Tab */}
      {activeTab === 'Referrals' && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h4 className="font-semibold text-gray-800">Referral Management</h4>
            <button onClick={() => setShowReferralModal(true)} className="bg-teal-500 text-white text-sm px-4 py-2 rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
              <i className="ri-add-line" />New Referral
            </button>
          </div>
          {child.referrals.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-share-forward-line text-4xl mb-2 block" />
              <p className="text-sm">No referrals yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {child.referrals.map((ref, i) => (
                <div key={i} className="flex items-start gap-4 bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-government-line text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-800">{ref.agency}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{ref.purpose}</p>
                    <p className="text-xs text-gray-400 mt-1">Date: {ref.date}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${ref.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {ref.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Inter-Agency Notes Tab */}
      {activeTab === 'Inter-Agency Notes' && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h4 className="font-semibold text-gray-800 mb-6">Inter-Agency Coordination Notes</h4>
          {child.interAgencyNotes.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-team-line text-4xl mb-2 block" />
              <p className="text-sm">No inter-agency notes yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {child.interAgencyNotes.map((note, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{note.agency}</p>
                      <p className="text-xs text-gray-400">Officer: {note.officer} · {note.date}</p>
                    </div>
                    <i className="ri-building-line text-gray-300 text-xl" />
                  </div>
                  <p className="text-sm text-gray-700">{note.note}</p>
                </div>
              ))}
            </div>
          )}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-700 mb-3">Add Coordination Note</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <input placeholder="Agency name" className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500" />
              <input placeholder="Officer name" className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500" />
            </div>
            <textarea placeholder="Coordination note..." rows={3} maxLength={500} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-none mb-2" />
            <div className="flex justify-end">
              <button className="bg-teal-500 text-white text-sm px-4 py-2 rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap">Add Note</button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Archive Case</h3>
            <p className="text-sm text-gray-500 mb-4">Are you sure you want to archive this case? It will be moved to the archives and can be retrieved later.</p>
            <textarea placeholder="Reason for archiving..." rows={3} maxLength={500} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-none mb-4" />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowArchiveModal(false)} className="text-sm border border-gray-200 px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={() => { setShowArchiveModal(false); navigate('/child-profiles'); }} className="text-sm bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-900 transition-colors cursor-pointer whitespace-nowrap">Archive Case</button>
            </div>
          </div>
        </div>
      )}

      {/* Referral Modal */}
      {showReferralModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Create Referral</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Referring Agency</label>
                <input value={referralAgency} onChange={(e) => setReferralAgency(e.target.value)} placeholder="e.g. PNP WCPD, DOJ PAO" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Purpose</label>
                <textarea value={referralPurpose} onChange={(e) => setReferralPurpose(e.target.value)} placeholder="Purpose of referral..." rows={3} maxLength={500} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-none" />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-4">
              <button onClick={() => setShowReferralModal(false)} className="text-sm border border-gray-200 px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={() => setShowReferralModal(false)} className="text-sm bg-teal-500 text-white px-4 py-2 rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap">Submit Referral</button>
            </div>
          </div>
        </div>
      )}
      
      {showEditModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl">

            <h3 className="text-lg font-bold text-gray-900 mb-4">Edit Child Profile</h3>

            <div className="grid grid-cols-2 gap-3">
              <input value={editForm.firstName} onChange={(e)=>updateEdit('firstName', e.target.value)} placeholder="First Name" className="input" />
              <input value={editForm.lastName} onChange={(e)=>updateEdit('lastName', e.target.value)} placeholder="Last Name" className="input" />

              <input value={editForm.middleName} onChange={(e)=>updateEdit('middleName', e.target.value)} placeholder="Middle Name" className="input" />

              <input type="date" value={editForm.birthDate} onChange={(e)=>updateEdit('birthDate', e.target.value)} className="input" />

              <input type="number" value={editForm.age} onChange={(e)=>updateEdit('age', e.target.value)} placeholder="Age" className="input" />

              <select value={editForm.gender} onChange={(e)=>updateEdit('gender', e.target.value)} className="input">
                <option>Male</option>
                <option>Female</option>
              </select>

              <input value={editForm.nationality} onChange={(e)=>updateEdit('nationality', e.target.value)} placeholder="Nationality" className="input" />
              <input value={editForm.religion} onChange={(e)=>updateEdit('religion', e.target.value)} placeholder="Religion" className="input" />

              <input value={editForm.address} onChange={(e)=>updateEdit('address', e.target.value)} placeholder="Address" className="input col-span-2" />

              <input value={editForm.barangay} onChange={(e)=>updateEdit('barangay', e.target.value)} placeholder="Barangay" className="input" />
              <input value={editForm.municipality} onChange={(e)=>updateEdit('municipality', e.target.value)} placeholder="Municipality" className="input" />
              <input value={editForm.province} onChange={(e)=>updateEdit('province', e.target.value)} placeholder="Province" className="input" />
              
              <input value={editForm.guardianName} onChange={(e)=>updateEdit('guardianName', e.target.value)} placeholder="Guardian Name" className="input" />
              <input value={editForm.guardianContact} onChange={(e)=>updateEdit('guardianContact', e.target.value)} placeholder="Contact" className="input" />

              <input
                value={editForm.medicalConditions || ''}
                onChange={(e)=>updateEdit('medicalConditions', e.target.value)}
                placeholder="Medical Conditions (comma separated)"
                className="input col-span-2"
              />

              <input
                value={editForm.medications || ''}
                onChange={(e)=>updateEdit('medications', e.target.value)}
                placeholder="Medications (comma separated)"
                className="input col-span-2"
              />

            </div>

            <div className="flex gap-3 justify-end mt-5">
              <button
                onClick={() => setShowEditModal(false)}
                className="text-sm border border-gray-200 px-4 py-2 rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={handleUpdate}
                className="text-sm bg-teal-500 text-white px-4 py-2 rounded-lg hover:bg-teal-600"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
